import { useEffect, useMemo, useState } from 'react';
import '../../assets/css/case-home.css';
import '../../assets/css/case-property.css';
// @ts-ignore
import { EditableToken, useContentToken, useSlotGroup, EditableSlotGroup, useEditMode } from '@vibesites/runtime/react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { isOfferActive } from './propertyUtils';
// @ts-ignore
import { Dialog, DialogContent, DialogTitle, VibesitesImage } from '@vibesites/ui-primitives';

const AUTO_SHOW_DELAY_MS = 1200;
const STORAGE_KEY_PREFIX = 'case-corporate-special-dismissed:';
export const CORPORATE_SPECIAL_OFFER_EVENT = 'open-corporate-special-offer';

const IMAGE_SLOT_DEF: SlotGroupDefinition = {
  label: 'Popup Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: '',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Company special offer image',
    },
  },
};

export function openCorporateSpecialOffer() {
  window.dispatchEvent(new CustomEvent(CORPORATE_SPECIAL_OFFER_EVENT));
}

function buildOfferKey(
  title: string,
  description: string,
  disclaimer: string,
  startDate: string,
  endDate: string,
): string {
  return `${title}::${description}::${disclaimer}::${startDate}::${endDate}`;
}

function getInactiveReason(
  isEnabled: boolean,
  hasContent: boolean,
  isWithinDateRange: boolean,
  startDate: string,
  endDate: string,
): string {
  if (!isEnabled) return 'Hidden on site';
  if (!hasContent) return 'Add title or description';
  if (isWithinDateRange) return '';

  const start = startDate.trim();
  const end = endDate.trim();
  const now = new Date();
  if (start) {
    const startAt = new Date(start);
    if (!Number.isNaN(startAt.getTime()) && now < startAt) return 'Scheduled';
  }
  if (end) {
    const endAt = new Date(end);
    if (!Number.isNaN(endAt.getTime()) && now > endAt) return 'Ended';
  }
  return 'Outside date range';
}

export function CorporateSpecialOffer() {
  const editMode = useEditMode();
  const isEditMode = editMode?.isEditMode ?? false;

  const enabledValue = useContentToken('corporatespecialoffer.enabled') || contentSchema.tokens['corporatespecialoffer.enabled'].defaultValue;
  const isEnabled = enabledValue === 'true';

  const badgeText = useContentToken('corporatespecialoffer.badge_text') || contentSchema.tokens['corporatespecialoffer.badge_text'].defaultValue;
  const showImageValue = useContentToken('corporatespecialoffer.show_image') || contentSchema.tokens['corporatespecialoffer.show_image'].defaultValue;
  const showImage = showImageValue === 'true';

  const title = useContentToken('corporatespecialoffer.title') || contentSchema.tokens['corporatespecialoffer.title'].defaultValue || 'Company-Wide Special';
  const description = useContentToken('corporatespecialoffer.description') || contentSchema.tokens['corporatespecialoffer.description'].defaultValue || '';
  const disclaimer = useContentToken('corporatespecialoffer.disclaimer') || contentSchema.tokens['corporatespecialoffer.disclaimer'].defaultValue || '';
  const startDate = useContentToken('corporatespecialoffer.start_date') || contentSchema.tokens['corporatespecialoffer.start_date'].defaultValue || '';
  const endDate = useContentToken('corporatespecialoffer.end_date') || contentSchema.tokens['corporatespecialoffer.end_date'].defaultValue || '';

  const imageSlot = useSlotGroup('corporatespecialoffer.popup_image', IMAGE_SLOT_DEF);

  const [open, setOpen] = useState(false);

  const hasContent = Boolean(title.trim() || description.trim());
  const isWithinDateRange = isOfferActive({ start_date: startDate, end_date: endDate });
  const isLiveOnSite = isEnabled && hasContent && isWithinDateRange;
  const shouldRender = isEditMode || isLiveOnSite;
  const inactiveReason = getInactiveReason(isEnabled, hasContent, isWithinDateRange, startDate, endDate);
  const showInactiveState = isEditMode && !isLiveOnSite;

  const offerKey = useMemo(
    () => buildOfferKey(title, description, disclaimer, startDate, endDate),
    [description, disclaimer, endDate, startDate, title],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleOpen = () => setOpen(true);
    window.addEventListener(CORPORATE_SPECIAL_OFFER_EVENT, handleOpen);
    return () => window.removeEventListener(CORPORATE_SPECIAL_OFFER_EVENT, handleOpen);
  }, []);

  useEffect(() => {
    if (isEditMode || !isEnabled || !hasContent || !isWithinDateRange || !offerKey || typeof window === 'undefined') {
      setOpen(false);
      return;
    }

    const storageKey = `${STORAGE_KEY_PREFIX}${offerKey}`;
    const dismissed = window.sessionStorage.getItem(storageKey) === 'true';
    if (dismissed) return;

    const timer = window.setTimeout(() => {
      setOpen(true);
    }, AUTO_SHOW_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [hasContent, isEditMode, isEnabled, isWithinDateRange, offerKey]);

  const handleClose = () => {
    setOpen(false);
    if (typeof window !== 'undefined' && offerKey) {
      window.sessionStorage.setItem(`${STORAGE_KEY_PREFIX}${offerKey}`, 'true');
    }
  };

  if (!shouldRender) return null;

  const bannerPrimaryText = title || description || 'Company-Wide Special';

  return (
    <div
      className="case-corporate-special"
      style={{ '--prop-primary': 'var(--case-primary)' } as React.CSSProperties}
    >
      <EditableToken
        path="corporatespecialoffer.enabled"
        type="text"
        label="Show Special Offer (true / false)"
        as="span"
        style={{ display: 'none' }}
        defaultValue={contentSchema.tokens['corporatespecialoffer.enabled'].defaultValue}
      >
        {enabledValue}
      </EditableToken>
      <EditableToken
        path="corporatespecialoffer.start_date"
        type="datetime"
        label="Start Date"
        as="span"
        style={{ display: 'none' }}
        defaultValue={contentSchema.tokens['corporatespecialoffer.start_date'].defaultValue}
      >
        {startDate}
      </EditableToken>
      <EditableToken
        path="corporatespecialoffer.end_date"
        type="datetime"
        label="End Date"
        as="span"
        style={{ display: 'none' }}
        defaultValue={contentSchema.tokens['corporatespecialoffer.end_date'].defaultValue}
      >
        {endDate}
      </EditableToken>

      <div className={`case-corporate-promo-banner${showInactiveState ? ' case-corporate-promo-banner--inactive' : ''}`}>
        <div className="case-corporate-promo-inner">
          {showInactiveState && (
            <div className="case-corporate-promo-status" aria-live="polite">
              <span className="case-corporate-promo-status-icon" aria-hidden="true">
                <svg viewBox="0 0 640 512" width="1em" height="1em" fill="currentColor">
                  <path d="M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L525.6 386.7c39.6-40.4 63.4-95.9 63.4-154.7 0-123.7-100.3-224-224-224-58.8 0-114.3 23.8-154.7 63.4L38.8 5.1zM233.4 357.5c25.1 8.4 52.2 8.4 77.3 0l89.6-29.9c9.2-3.1 14.3-12.7 11.2-21.9l-8.4-25.2c-3.1-9.2-12.7-14.3-21.9-11.2l-89.6 29.9c-8.5 2.8-17.4 4.2-26.4 4.2-35.3 0-64-28.7-64-64 0-8.9 1.8-17.4 5.1-25.1l102.3 80.3z" />
                </svg>
              </span>
              <span>{inactiveReason}</span>
            </div>
          )}
          <button
            type="button"
            className="case-corporate-promo-item"
            onClick={() => setOpen(true)}
            aria-label={showInactiveState ? `Preview company special offer (${inactiveReason.toLowerCase()})` : 'Open company special offer'}
          >
            <span className="prop-promo-icon" aria-hidden="true">
              <svg viewBox="0 0 640 512" width="1em" height="1em" fill="currentColor">
                <path d="M0 48C0 21.5 21.5 0 48 0H592c26.5 0 48 21.5 48 48V464c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V48zM352 352c0 10.1 6 19.2 15.3 23.1s20.1 1.5 27.5-6L448 315.7l53.3 53.3c7.3 7.5 18.2 9.9 27.5 6S544 362 544 352V64H352V352z" />
              </svg>
            </span>
            <EditableToken
              path="corporatespecialoffer.title"
              type="text"
              label="Title"
              as="span"
              className="prop-promo-label"
              defaultValue={contentSchema.tokens['corporatespecialoffer.title'].defaultValue}
            >
              {bannerPrimaryText}
            </EditableToken>
            <EditableToken
              path="corporatespecialoffer.description"
              type="text"
              label="Description"
              as="span"
              className={`prop-promo-copy${title && description ? '' : ' sr-only'}`}
              defaultValue={contentSchema.tokens['corporatespecialoffer.description'].defaultValue}
            >
              {description}
            </EditableToken>
          </button>
        </div>
      </div>

      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            handleClose();
            return;
          }
          setOpen(true);
        }}
      >
        <DialogContent
          className={`prop-offer-dialog-content p-0 gap-0 border-0 overflow-hidden ${showImage ? 'sm:max-w-[960px]' : 'sm:max-w-[860px]'}`}
          style={{ borderRadius: '1rem' }}
        >
          <DialogTitle
            className="sr-only"
            style={{
              position: 'absolute',
              width: '1px',
              height: '1px',
              padding: 0,
              margin: '-1px',
              overflow: 'hidden',
              clip: 'rect(0,0,0,0)',
              whiteSpace: 'nowrap',
              borderWidth: 0,
            }}
          >
            {title}
          </DialogTitle>
          <div className={`prop-offer-dialog ${showImage ? 'prop-offer-dialog--with-image' : ''}`}>
            <EditableToken
              path="corporatespecialoffer.show_image"
              type="text"
              label="Show Image (true / false)"
              as="span"
              style={{ display: 'none' }}
              defaultValue={contentSchema.tokens['corporatespecialoffer.show_image'].defaultValue}
            >
              {showImageValue}
            </EditableToken>

            {showImage && imageSlot.image && (
              <EditableSlotGroup
                groupPath="corporatespecialoffer.popup_image"
                definition={IMAGE_SLOT_DEF}
                className="prop-offer-dialog-image-wrapper"
              >
                <div className="prop-offer-dialog-image">
                  <VibesitesImage
                    url={imageSlot.image}
                    alt={imageSlot.alt || 'Company special offer image'}
                    sizes="(min-width: 768px) 400px, 100vw"
                  />
                </div>
              </EditableSlotGroup>
            )}

            <div className="prop-offer-carousel prop-offer-carousel--dialog">
              <div className="prop-offer-carousel-viewport">
                <div className="prop-offer-carousel-track">
                  <div className="prop-offer-carousel-slide prop-offer-dialog-slide">
                    <div className="prop-offer-dialog-copy">
                      <EditableToken
                        path="corporatespecialoffer.badge_text"
                        type="text"
                        label="Popup Badge Text"
                        as="span"
                        className="prop-offer-dialog-badge"
                        defaultValue={contentSchema.tokens['corporatespecialoffer.badge_text'].defaultValue}
                      >
                        {badgeText}
                      </EditableToken>
                      <h2 className="prop-offer-dialog-title">{title}</h2>
                      {description && <p className="prop-offer-dialog-body">{description}</p>}
                      <EditableToken
                        path="corporatespecialoffer.disclaimer"
                        type="text"
                        label="Disclaimer"
                        as="p"
                        className={`prop-offer-dialog-disclaimer${disclaimer ? '' : ' sr-only'}`}
                        defaultValue={contentSchema.tokens['corporatespecialoffer.disclaimer'].defaultValue}
                      >
                        {disclaimer}
                      </EditableToken>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'CorporateSpecialOffer',
  displayName: 'Corporate Special Offer',
  tokens: {
    'corporatespecialoffer.enabled': {
      label: 'Show Special Offer (true / false)',
      type: 'text',
      defaultValue: 'true',
      description: 'Set to false to hide the banner and popup on the live site.',
    },
    'corporatespecialoffer.title': {
      label: 'Title',
      type: 'text',
      defaultValue: 'Company-Wide Special',
    },
    'corporatespecialoffer.description': {
      label: 'Description',
      type: 'text',
      defaultValue: 'Limited-time savings across select Case & Associates communities.',
    },
    'corporatespecialoffer.disclaimer': {
      label: 'Disclaimer',
      type: 'text',
      defaultValue: 'Terms and conditions apply. Offer availability varies by property.',
    },
    'corporatespecialoffer.start_date': {
      label: 'Start Date',
      type: 'datetime',
      defaultValue: '',
      description: 'Optional start date/time. Leave blank for no start bound. Evaluated in the visitor\'s local timezone, matching Multihub special offers.',
    },
    'corporatespecialoffer.end_date': {
      label: 'End Date',
      type: 'datetime',
      defaultValue: '',
      description: 'Optional end date/time. Leave blank for no end bound. Evaluated in the visitor\'s local timezone, matching Multihub special offers.',
    },
    'corporatespecialoffer.badge_text': {
      label: 'Popup Badge Text',
      type: 'text',
      defaultValue: 'Limited Time Offer',
    },
    'corporatespecialoffer.show_image': {
      label: 'Show Image (true / false)',
      type: 'text',
      defaultValue: 'false',
    },
  },
  slotGroups: {
    'corporatespecialoffer.popup_image': IMAGE_SLOT_DEF,
  },
};

(CorporateSpecialOffer as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default CorporateSpecialOffer;
